<form
	class="ada-container ada-col-1 ada-vspacing-small"
	(submit)="$event.preventDefault()"
>
	@if (errorMessage()) {
		<ada-alert
			data-testid="ada-login-form-error-message"
			type="error"
			[isClosable]="false"
			[title]="errorMessage()"
		/>
	}
	<ada-card
		[collapseEnabled]="false"
		[fullscreenEnabled]="false"
	>
		<span
			ada-color="foreground"
			data-container-selector="ada-card-header"
		>
			Log in with Email
		</span>
		<ada-container
			data-container-selector="ada-card-body"
			verticalSpacing="small"
		>
			<ada-container>
				<ada-label
					content="Email"
					[associatedWith]="Email"
				/>
				<ada-input
					#Email
					data-testid="ada-login-form-email"
					fieldName="Email"
					id="ada-login-form-email"
					type="email"
					[showAsFullWidth]="true"
					[(value)]="login.username"
				/>
			</ada-container>
			<ada-container>
				<ada-label
					content="Password"
					[associatedWith]="Password"
				/>
				<ada-input
					#Password
					data-testid="ada-login-form-password"
					fieldName="Password"
					id="ada-login-form-password"
					type="password"
					[showAsFullWidth]="true"
					[(value)]="login.password"
				/>
			</ada-container>
			<ada-checkbox
				data-testid="remember-email-checkbox"
				label="Remember my email"
				[(isChecked)]="rememberEmail"
			/>
		</ada-container>
		<ada-container
			data-container-selector="ada-card-footer"
			verticalSpacing="small"
		>
			<ada-push-button
				#NativeLoginButton
				data-testid="login-form-native-login-button"
				look="solid"
				text="Log in"
				[adaClick]="nativeLogin | adaBindMethod: this : NativeLoginButton"
				[showAsFullWidth]="true"
			/>
			@if ((auth.serverMetadata | async)?.cloudPortal) {
				<ada-push-button
					#SignupButton
					data-testid="login-form-sign-up-button"
					look="Outline"
					text="Sign Up"
					[adaClick]="signup | adaBindMethod: this"
					[showAsFullWidth]="true"
				/>
			}
			<ada-clickable
				class="ada-text-align-center"
				data-testid="forgot-password-button"
				horizontalAlignment="center"
				[adaClick]="forgotPassword | adaBindMethod: this"
			>
				<span ada-color="foreground">Forgot Password?</span>
			</ada-clickable>
		</ada-container>
	</ada-card>
	<ada-container>
		@if ((auth.serverMetadata | async)?.cloudPortal !== true) {
			<ada-push-button
				#ActiveDirectoryLoginButton
				data-testid="login-form-active-directory-login-button"
				look="Outline"
				text="Log in with Active Directory"
				[adaClick]="activeDirectoryLogin | adaBindMethod: this : ActiveDirectoryLoginButton"
				[showAsFullWidth]="true"
			/>
		}

		@if (isIdentityProvidersLoading()) {
			<div
				class="ada-container ada-col-0 ada-colspan-1 ada-flex-justify-center"
				style.padding="{{ ui.Spacing.SPACING_LARGE }}"
			>
				<ada-spinner tooltip="Loading identity providers..." />
			</div>
		} @else {
			@if (identityProviderError(); as _identityProviderError) {
				<ada-alert
					type="error"
					[isClosable]="false"
					[title]="_identityProviderError"
				/>
			} @else {
				@for (_oidcProvider of oidcProviders | async; track _oidcProvider.objectId) {
					<ada-push-button
						#OidcLoginButton
						class="ada-oidc-login-button"
						look="Outline"
						[adaClick]="oidcLogin | adaBindMethod: this : _oidcProvider : OidcLoginButton"
						[attr.data-testid]="'oidc-provider-login-button-' + _oidcProvider.objectId"
						[imageUrl]="_oidcProvider.logoData"
						[showAsFullWidth]="true"
						[text]="'Log in with ' + _oidcProvider.name"
					/>
				}

				@for (_samlProvider of samlProviders | async; track _samlProvider.objectId) {
					<ada-push-button
						#SamlLoginButton
						class="ada-saml-login-button"
						look="Outline"
						[adaClick]="samlLogin | adaBindMethod: this : _samlProvider : SamlLoginButton"
						[attr.data-testid]="'saml-provider-login-button-' + _samlProvider.objectId"
						[imageUrl]="_samlProvider.logoData"
						[showAsFullWidth]="true"
						[text]="'Log in with ' + _samlProvider.name"
					/>
				}
			}
		}

		@if ((auth.serverMetadata | async)?.fedramp) {
			<ada-horizontal-separator padding="{{ ui.Spacing.SPACING_LARGE }} 0px 0px 0px" />
			<ada-text
				content="This system contains U.S. Government information. By using this system, you acknowledge that your activity may be monitored, recorded, and audited. Unauthorized use is prohibited and can be subject to civil and criminal penalties. Your usage implies consent to this surveillance, and by clicking login, you confirm your understanding and acceptance of these terms."
				data-testid="login-form-fedramp-disclaimer"
				padding="{{ ui.Spacing.SPACING_SMALL }} 0px"
			/>
		}
	</ada-container>
</form>

@defer (when isActiveDirectoryConfigError() && platform.FIREFOX) {
	<ada-overlay
		[isOpen]="true"
		[showCancelButton]="false"
		[title]="'Check Browser Configuration'"
	>
		<div
			*adaTemplateMarker
			style.padding="{{ ui.Spacing.SPACING_LARGE }}"
		>
			<p ada-color="foreground">
				It looks like you're using Mozilla Firefox. To use Active Directory authentication with Firefox, please ensure your browser is
				properly configured:
			</p>
			<ul>
				<li>
					<span ada-color="foreground"> Navigate to <code>about:config</code> in the Firefox address bar </span>
				</li>
				<li>
					<span ada-color="foreground"> Acknowledge the displayed warning and continue </span>
				</li>
				<li>
					<span ada-color="foreground"> Search for <code>network.negotiate-auth.trusted-uris</code> and edit the value </span>
				</li>
				<li>
					<span ada-color="foreground">
						Enter the FQDNs of the servers for which you'd like to use AD authentication, separated by commas and spaces
					</span>
					<ul>
						<li>
							<span ada-color="foreground">
								To enable this server, enter <code>{{ env.fqdn }}</code>
							</span>
						</li>
					</ul>
				</li>
				<li>
					<span ada-color="foreground"> Confirm the change and attempt Active Directory login again </span>
				</li>
			</ul>
		</div>
	</ada-overlay>
}
